<script setup>
import { computed } from 'vue';
import { useAuth } from '../../composables/useAuth.js';
import { usePlayers } from '../../composables/usePlayers.js';
import { fmtDate, fmtEuro, fmtPercent } from '../../domain/format.js';
import { CONTRACT_TYPES, hasVisibleContractData } from '../../domain/contracts.js';
import TransferHistory from '../TransferHistory.vue';
import DateField from '../DateField.vue';

/* Transferdaten eines abgegebenen Spielers.
 *
 * Kaufmännische Angaben — admin, board und data (Entscheidung Bastian,
 * 2026-09-25). Seit Stufe 3 (Teil 2) stehen sie in der Tabelle
 * player_contracts, nicht mehr im Datenblock des Spielers; die Datenbank
 * schützt sie dort einzeln: staff und guest bekommen keine Zeile zu sehen.
 * Die Daten hängen als `player.contract` im Speicher (domain/contracts.js) und
 * werden mit dem Speichern-Knopf des Panels geschrieben. Fehlt `contract`,
 * konnten sie nicht geladen werden — dann wird nichts angezeigt und nichts
 * gespeichert, damit keine leere Fassung echte Daten überschreibt.
 *
 * Verein und Logo (player.currentClub) werden hier nicht mehr angezeigt oder bearbeitet,
 * nur noch in Player Info ("Now at", ClubField.vue). contract.club
 * (Spalte player_contracts.club) wird ebenfalls weder angezeigt noch bearbeitet,
 * aber auch nicht verändert — siehe CHANGELOG 2026-10-04.
 *
 * Der Reiter selbst ist seit 2026-10-04 für ALLE Rollen sichtbar (Bereich
 * "players" in domain/playerTabs.js), weil hier die Transferhistorie steht, die
 * auch Gäste sehen sollen. Die Historie sieht daher jede Rolle;
 * die Vertragsfelder (Art, Ende, Ablöse, Sell-on) und der Hinweis darunter nur
 * mit Leserecht auf contracts (admin, board, data) — diese Prüfung steht hier,
 * nicht mehr im Panel.
 *
 * Reihenfolge: Transferhistorie (TransferHistory.vue), Vertragsfelder.
 */

const props = defineProps({
  player: { type: Object, required: true },
});

const { can } = useAuth();
const { contractsError } = usePlayers();
const canEdit = computed(() => can('contracts', 'write'));
const canRead = computed(() => can('contracts', 'read'));

const TYPES = CONTRACT_TYPES;

const ti = computed(() => props.player.contract);

/* Was der Reiter zeigt (Entscheidung Bastian, 2026-10-04):
   - transferred: Historie (bei leerer Historie mit Hinweistext), Vertragsfelder
   - alle anderen Status: nur die Historie
   - Ausnahme: Hat der Spieler schon befüllte Vertragsdaten, zeigt der Reiter sie weiter, für
     Rollen mit Leserecht auf contracts — damit nichts Vorhandenes unsichtbar wird. Dann steht
     auch hier der Kasten mit Titel; das Vereinsfeld bleibt bei Nicht-transferred weg. */
const isTransferred = computed(() => props.player.status === 'transferred');
const hasContractData = computed(() => !!ti.value && hasVisibleContractData(ti.value));
const showContract = computed(() => canRead.value && (isTransferred.value || hasContractData.value));
/* Kein Vereinsfeld mehr im Reiter (Entscheidung Bastian, 2026-10-07): Der aktuelle Verein
   wird nur noch in Player Info unter "Now at" bearbeitet. Der Kasten steht nur für Vertragsfelder. */
const showCard = computed(() => showContract.value);
</script>

<template>
  <div>
    <div :class="{ 'transfer-info': showCard }">
      <h3 v-if="showCard">{{ $t('transfer.title') }}</h3>

      <!-- Transferhistorie: für alle Rollen sichtbar (Gäste eingeschlossen). Reihenfolge im
           Reiter: Historie, Vertragsfelder. -->
      <TransferHistory :player="player" :show-empty="isTransferred" />

      <!-- Ab hier kaufmännisch: nur mit Leserecht auf contracts (admin, board, data) -->
      <template v-if="showContract">
      <div v-if="!ti" class="feed-empty">
        {{ contractsError ? $t('errors.loadFailed') : $t('common.loading') }}
      </div>

      <div v-else-if="canEdit" class="ti-grid">
        <div class="ti-field">
          <label>{{ $t('transfer.type') }}</label>
          <select v-model="ti.type">
            <option value="">—</option>
            <option v-for="tp in TYPES" :key="tp" :value="tp">{{ $t(`transfer.${tp}`) }}</option>
          </select>
        </div>
        <div class="ti-field">
          <label>{{ $t('transfer.contractEnd') }}</label>
          <DateField v-model="ti.contractEnd" />
        </div>
        <div class="ti-field">
          <label>{{ $t('transfer.fee') }}</label>
          <input v-model.number="ti.fee" type="number" inputmode="decimal" min="0" step="1" placeholder="65000" />
        </div>
        <div class="ti-field">
          <label>{{ $t('transfer.sellOn') }}</label>
          <input v-model.number="ti.sellOn" type="number" inputmode="decimal" min="0" max="100" step="0.1" placeholder="10" />
        </div>
      </div>

      <!-- Nur-Lesen-Ansicht für Rollen ohne Schreibrecht -->
      <div v-else class="ti-grid">
        <div class="info-item">
          <label>{{ $t('transfer.type') }}</label>
          <span>{{ ti.type ? $t(`transfer.${ti.type}`) : '—' }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('transfer.contractEnd') }}</label>
          <span>{{ fmtDate(ti.contractEnd) || '—' }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('transfer.fee') }}</label>
          <span>{{ fmtEuro(ti.fee) || '—' }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('transfer.sellOn') }}</label>
          <span>{{ fmtPercent(ti.sellOn) || '—' }}</span>
        </div>
      </div>
      </template>
    </div>

    <div v-if="showContract" class="hint">{{ $t('transfer.hint') }}</div>
  </div>
</template>

<style scoped>
.hint {
  margin-top: 10px;
}
</style>
